import { Card, CardActionArea, CardContent, Chip, Stack, Typography, IconButton, Tooltip } from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import { useTranslation } from 'react-i18next';
import type { KanbanCard } from '@pm/shared';
import { normalizeKanbanState } from '../../../../api/hooks/useKanban';
import { RichTextView, isRichTextEmpty } from '../../../../components/pm/RichTextEditor';

interface Props {
  card: KanbanCard;
  /** true = bearbeitbares Board (Drag/Edit/Actions aktiv). */
  active: boolean;
  /** Optisches Feedback während des Ziehens dieser Karte. */
  dragging?: boolean;
  /** Herkunfts-Arbeitspaket (nur Projekt-Modus): z. B. „TJ-AP-1". */
  apChipLabel?: string;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onEdit?: () => void;
  onToggleHidden?: () => void;
  onRemove?: () => void;
  /** P-17: Karte abschließen (state → COMPLETED). */
  onComplete?: () => void;
  /** P-17: abgeschlossene Karte wiedereröffnen (state → IN_PROGRESS). */
  onReopen?: () => void;
}

/** Einzelne Kanban-Karte (Titel, Beschreibung, Herkunft, Aktionen) — geteilt von
 *  KanbanBoardView (AP-Modus) und ProjectKanbanView (Projekt-Modus). */
export function KanbanCardItem({ card, active, dragging, apChipLabel, onDragStart, onDragEnd, onEdit, onToggleHidden, onRemove, onComplete, onReopen }: Props) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');

  return (
    <Card
      variant="outlined"
      draggable={active}
      onDragStart={active ? onDragStart : undefined}
      onDragEnd={onDragEnd}
      sx={{
        cursor: active ? 'grab' : 'default',
        opacity: card.hidden ? 0.45 : dragging ? 0.4 : 1,
        '&:active': active ? { cursor: 'grabbing' } : {},
        '&:hover .kanban-card-actions': { opacity: 1 },
      }}
    >
      <CardActionArea component="div" disableRipple={!active} onClick={active ? onEdit : undefined}>
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>{card.title}</Typography>
            {active && (
              <Stack direction="row" className="kanban-card-actions" sx={{ opacity: 0, transition: 'opacity .15s' }}>
                {normalizeKanbanState(card.state) === 'COMPLETED' ? (
                  <Tooltip title={t('kanban.reopen', 'Wiedereröffnen')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onReopen?.(); }}>
                      <ReplayIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('kanban.complete', 'Abschließen')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onComplete?.(); }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={card.hidden ? t('kanban.unhide', 'Einblenden') : t('kanban.hide', 'Ausblenden')}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleHidden?.(); }}>
                    {card.hidden ? <VisibilityIcon sx={{ fontSize: 16 }} /> : <VisibilityOffIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={tc('actions.delete', 'Löschen')}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove?.(); }}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
          {!isRichTextEmpty(card.description) && (
            <RichTextView
              html={card.description}
              sx={{
                mt: 0.5, color: 'var(--pm-fg-muted)', fontSize: '0.78rem',
                display: '-webkit-box', overflow: 'hidden', WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical', '& p': { m: 0 },
              }}
            />
          )}
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {apChipLabel && (
              <Chip label={apChipLabel} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
            )}
            {card.source === 'auto_r' && (
              <Chip label="R" size="small" color="primary" variant="filled" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
            )}
            {card.person && (
              <Chip icon={<PersonIcon sx={{ fontSize: 14 }} />} label={card.person} size="small" variant="outlined" sx={{ height: 20 }} />
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
